import { AddressValidationStatus } from '@models/customer';
import { MouseEvent, useRef, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { TextField } from '@components/textField';
import { Button } from '@components/button';
import { NoMatchingAddress } from '@pages/NewQuote/location/NoMatchingAddress';
import { useValidPhysicalAddress } from '@services/validation';
import { EnteredAddress, AddressInput } from '@models/validation';
import { Select } from '@components/select';
import { AddressInputWithRequiredId } from '@pages/QuoteDetails/dialogs/MultiLocationDialog/MultiLocationDialog';

type ManualAddressEntryTypes = {
  handleSelectAddress: (address: EnteredAddress, id: string) => void;
  setAddressInputArray?: React.Dispatch<React.SetStateAction<AddressInputWithRequiredId[]>>;
  updateAddress?: (data: AddressInput) => void;
  address: AddressInput;
  availableStateSelections: { label: string; value: string }[];
  allowStateSelection: boolean;
  buttonText: string;
  handleCancel?: () => void;
};

export const ManualAddressEntry = ({
  address,
  setAddressInputArray,
  updateAddress,
  handleSelectAddress,
  availableStateSelections,
  allowStateSelection,
  buttonText,
  handleCancel,
}: ManualAddressEntryTypes) => {
  const { validPhysicalAddress, refetchAddress } = useValidPhysicalAddress(address);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const anchorRef = useRef(null);
  const [showNoMatchingAddress, setShowNoMatchingAddress] = useState<boolean>(false);

  const isDisabled = Object.entries(address)
    .filter(
      ([k, _]) =>
        k !== 'index' &&
        k !== 'id' &&
        k !== 'street2' &&
        k !== 'locationName' &&
        k !== 'manualEntry' &&
        k !== 'careOf' &&
        k !== 'isAddressValid',
    )
    .some(([_, v]) => !v);

  const handlePopoverClose = () => {
    setShowNoMatchingAddress(false);
    setAnchorEl(null);
  };

  const handleAddressConfirm = () => {
    handleResetManualAddress();
  };

  const handleResetManualAddress = () => {
    if (setAddressInputArray) {
      setAddressInputArray((prev: AddressInputWithRequiredId[]) =>
        prev.map((el: AddressInputWithRequiredId) => {
          if (el.id === address.id) {
            return { ...el, manualEntry: false };
          }
          return el;
        }),
      );
    }
    if (updateAddress) updateAddress(address);
    if (handleCancel) handleCancel();
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | (Event & { target: { value: string; name: string } }),
    id?: string,
  ) => {
    if (setAddressInputArray) {
      setAddressInputArray((prev: AddressInputWithRequiredId[]) =>
        prev.map((el: AddressInputWithRequiredId) => {
          if (el.id === id) {
            return { ...el, [e.target.name]: e.target.value };
          }
          return el;
        }),
      );
    }
    if (updateAddress) updateAddress({ ...address, [e.target.name]: e.target.value });
  };

  const handlePopoverChange = () => {
    setAnchorEl(anchorRef.current);
  };

  const handlePopoverOpen = () => {
    setShowNoMatchingAddress(true);
  };

  const handleNoMatchingAddress = async (_: MouseEvent<HTMLElement>, id?: string) => {
    handlePopoverChange();
    refetchAddress().then((response) => {
      if (response.isSuccess) {
        const { status } = response.data;
        if (status.includes(AddressValidationStatus.ExactMatch)) {
          handleResetManualAddress();
        } else {
          handlePopoverOpen();
        }
      } else if (response.error && address) {
        console.warn('Error fetching address', response.error, 'Address: ', address, 'Id: ', id);
      }
    });
  };

  const handleInvalidAddress = () => {
    if (setAddressInputArray) {
      setAddressInputArray((prev: AddressInputWithRequiredId[]) =>
        prev.map((el: AddressInputWithRequiredId) => {
          if (el.id === address.id) {
            return { ...el, isAddressValid: false };
          }
          return el;
        }),
      );
    }
    if (updateAddress) updateAddress({ ...address, isAddressValid: false });
  };

  return (
    <>
      <TextField
        inputLabel='Street Address'
        name='street1'
        value={address.street1}
        onChange={(e) => handleInputChange(e, address.id)}
        sx={{ mb: 4, width: '100%' }}
      />
      <TextField
        inputLabel='Street 2'
        name='street2'
        value={address.street2}
        onChange={(e) => handleInputChange(e, address.id)}
        sx={{ mb: 4, width: '100%' }}
      />
      <Grid
        container
        ref={anchorRef}
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Grid size={{ xs: 5 }}>
          <TextField
            fullWidth
            inputLabel='City'
            name='city'
            value={address.city}
            onChange={(e) => handleInputChange(e, address.id)}
            sx={{ mb: 4 }}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <Select
            fullWidth
            inputLabel='State'
            name='state'
            items={availableStateSelections}
            value={address.state}
            disabled={!allowStateSelection}
            onChange={(e) => handleInputChange(e, address.id)}
            sx={{ mb: 4 }}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <TextField
            inputLabel='Zip'
            name='zipCode'
            value={address.zipCode}
            onChange={(e) => handleInputChange(e, address.id)}
            sx={{ mb: 4 }}
          />
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='bodySmall'
          onClick={handleResetManualAddress}
          sx={{
            fontWeight: 'bold',
            lineHeight: '14px',
            marginRight: '10px',
            cursor: 'pointer',
          }}
        >
          Cancel
        </Typography>
        <Button
          variant='text'
          sx={{ padding: '0', marginLeft: '10px' }}
          disabled={isDisabled}
          onClick={(e) => handleNoMatchingAddress(e, address.id)}
        >
          {buttonText}
        </Button>
      </Grid>
      <NoMatchingAddress
        id='no-matching-address-popover'
        aria-labelledby='use-this-address-button'
        addressOptions={validPhysicalAddress?.items}
        open={showNoMatchingAddress}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 400,
          horizontal: 'center',
        }}
        enteredAddress={{
          ...address,
        }}
        onAddressConfirm={handleAddressConfirm}
        onPopoverClose={handlePopoverClose}
        onSelectAddress={(address) => handleSelectAddress(address, address.id || '')}
        onInvalidAdress={handleInvalidAddress}
      />
    </>
  );
};
